import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi, useShowApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { PcrTestRun, PcrTestRunForm, PcrTestRunSearchForm } from 'entities/pcr_test_run'

type PcrTestRunsResponse = PagingResponse & {
    results: PcrTestRun[]
}

export function useFetchPcrTestRunsApi(searchForm: Form<PcrTestRunSearchForm>): IndexApiSet<PcrTestRunsResponse> & { execute: () => void } {
    const apiPath = 'pcr_test_runs/'
    const api = useIndexApi<PcrTestRunsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm.object })
    }

    useEffectSkipFirst(() => {
        execute()
    }, [api.pageSet.page, api.pageSet.perPage])

    return { ...api, execute: execute }
}

export function useDownloadCsvPcrTestRunsApi(searchForm: Form<PcrTestRunSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'pcr_test_runs/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'PcrTestRun', searchForm.object)
    }

    return { ...api, execute: execute }
}

type PcrTestRunResponse = BaseResponse & {
    pcrTestRun?: PcrTestRun
}
export function usePostPcrTestRunApi(): ApiSet<PcrTestRunResponse> & { execute: (form: Form<PcrTestRunForm>) => void } {
    const apiSet = usePostApi<PcrTestRunResponse, PcrTestRunForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<PcrTestRunForm>) => {
        const apiPath = `pcr_test_runs/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function useFetchPcrTestRunApi(): ApiSet<PcrTestRunResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<PcrTestRunResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `pcr_test_runs/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export type ProcessLog = {
    probe?: string
    ct?: number
    qc_type?: string
    product?: string
    min?: number
    max?: number
    result?: boolean
    action?: string
}
export type ProcessLogs = { [key: string]: ProcessLog[] }

export type PutPcrTestRunResponse = BaseResponse & {
    processLogs?: ProcessLogs
}

export function usePutPcrTestRunApi(): ApiSet<PutPcrTestRunResponse> & { execute: (form: Form<PcrTestRunForm>) => void } {
    const apiSet = usePutApi<PutPcrTestRunResponse, PcrTestRunForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<PcrTestRunForm>) => {
        const apiPath = `pcr_test_runs/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutPcrTestRunsApi(): ApiSet<BaseResponse> & { execute: (form: Form<PcrTestRunForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, PcrTestRunForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<PcrTestRunForm[]>) => {
        const apiPath = `pcr_test_runs/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeletePcrTestRunApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `pcr_test_runs/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export function useCalculationTestResultApi(): ApiSet<PutPcrTestRunResponse> & { execute: (id: number) => void } {
    const apiSet = usePutApi<PutPcrTestRunResponse, PcrTestRunForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `pcr_test_runs/${id}/calculation/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}
